import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import {
  BuildingOffice2Icon,
  ChevronDownIcon,
  EyeIcon,
  PlusCircleIcon,
  XMarkIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import Card from '../ui/Card'
import Button from '../ui/Button'
import PageHeader from '../layout/PageHeader'
import InlineSelect from '../ui/InlineSelect'
import Combobox from '../ui/Combobox'
import MultiSelect from '../ui/MultiSelect'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useFieldFocusHandler } from '../../hooks/useFieldFocusHandler'
import EditingReturnBanner, { backToLabelFor } from './EditingReturnBanner'
import { useProjects } from '../../contexts/ProjectsContext'
import { ProjectDetails } from '../../types'
import Badge from '../ui/Badge'
import { useCatalog } from '../../contexts/CatalogContext'
import PageLoader from '../ui/PageLoader'
import { pipeSetBlockedMessage, resizeArrayByCount } from '../../utils/collectionSizing'
import ConfirmDialog from '../ui/ConfirmDialog'
import { toast } from 'sonner'
import { createData360Project } from '../../api/data360'
import { DATA_360_DISPLAY_NAME, formatData360DisplayText } from '../../utils/data360DisplayName'
import { useLockedFieldDomProps } from '../../contexts/LockedFieldHintContext'

interface MaterialRow {
  id: string
  materialSpec: string
  typeGrade: string
  manufacture: string
  source: string
}

interface PipeSizeGroup {
  id: string
  nominalOd: string
  nominalOdUnit: string
  nominalThickness: string
  nominalThicknessRangeMin: string
  nominalThicknessRangeMax: string
  nominalThicknessUnit: string
  source: string
  bevelType: string
  backingRequirement: string
  backingMaterial: string
  materials: MaterialRow[]
}

const makeId = () => Math.random().toString(36).slice(2, 10)

const emptyMaterial = (): MaterialRow => ({
  id: makeId(),
  materialSpec: '',
  typeGrade: '',
  manufacture: '',
  source: ''
})

const emptyPipeGroup = (): PipeSizeGroup => ({
  id: makeId(),
  nominalOd: '',
  nominalOdUnit: 'in',
  nominalThickness: '',
  nominalThicknessRangeMin: '',
  nominalThicknessRangeMax: '',
  nominalThicknessUnit: 'mm',
  source: '',
  bevelType: '',
  backingRequirement: '',
  backingMaterial: '',
  materials: [emptyMaterial()]
})

export default function ProjectCreate() {
  const { id } = useParams()
  const initialGroup = emptyPipeGroup()
  const [pipeGroups, setPipeGroups] = useState<PipeSizeGroup[]>([initialGroup])
  const [expandedGroups, setExpandedGroups] = useState<string[]>([initialGroup.id])
  const [pipeSectionState, setPipeSectionState] = useState<
    Record<string, { metadata: boolean; bevel: boolean; materials: boolean }>
  >({
    [initialGroup.id]: { metadata: true, bevel: false, materials: false }
  })
  const [bevelPreview, setBevelPreview] = useState<PipeSizeGroup | null>(null)
  const { getProject, fetchProjectById, patchProjectInList, updateProject, deleteProject, loading: projectsLoading, refreshProjects } = useProjects()
  const navigate = useNavigate()
  const [editFetchLoading, setEditFetchLoading] = useState(Boolean(id))
  const [fetchedProject, setFetchedProject] = useState<ReturnType<typeof getProject>>(undefined)
  const projectToEdit = id ? (fetchedProject ?? getProject(id)) : undefined
  const { getLabelsMerged, addLabel } = useCatalog()
  const [pipeSetCountError, setPipeSetCountError] = useState<string | null>(null)
  const [savedWorkspaceJson, setSavedWorkspaceJson] = useState('')
  const pipeSetCountInputRef = useRef<HTMLInputElement | null>(null)
  const [projectDetails, setProjectDetails] = useState({
    name: projectToEdit?.name ?? '',
    projectNumber: projectToEdit?.projectNumber ?? '',
    contractors: projectToEdit?.contractors ?? (projectToEdit?.client ? [projectToEdit.client] : []),
    endClients: projectToEdit?.endClients ?? (projectToEdit?.client ? [projectToEdit.client] : []),
    applications: projectToEdit?.applications ?? (projectToEdit?.application ? [projectToEdit.application] : []),
    weldingCodes: projectToEdit?.weldingCodes ?? [],
    clientWeldingSpecs: projectToEdit?.clientWeldingSpecs ?? [],
    equipment: projectToEdit?.equipment ?? [],
    location: projectToEdit?.location ?? ''
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [attemptedSave, setAttemptedSave] = useState(false)
  const [activeSection, setActiveSection] = useState<'project' | 'pipes'>('project')
  const [pipeSetCountInput, setPipeSetCountInput] = useState(String(pipeGroups.length))
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [saveBusy, setSaveBusy] = useState(false)
  const [searchParams] = useSearchParams()
  const isLegacyEdit = Boolean(projectToEdit && !projectToEdit.data360ProjectId)
  const isData360Create = !projectToEdit
  const isD360Owned =
    Boolean(projectToEdit?.data360ProjectId) && projectToEdit?.createdFromApp !== 'WPS-Studio'
  const coreFieldsReadOnly = isD360Owned
  const data360LockedDomProps = useLockedFieldDomProps({
    locked: coreFieldsReadOnly,
    message: `Managed in ${DATA_360_DISPLAY_NAME}`
  })
  const canDeleteProject =
    !projectToEdit?.data360ProjectId || projectToEdit?.createdFromApp === 'WPS-Studio'

  // Slice 04 of pqr-preview-field-navigation. When the user clicks an
  // equipment row on the PQR Preview canvas they're routed here with
  // `?focusField=equipment&returnTo=...`. We scroll the equipment
  // MultiSelect into view, flash it amber, and surface the return
  // banner so the user can hop back to the Preview after editing.
  //
  // Slice 04 of wps-preview-pixel-parity extends the same registry to
  // every top-meta cell rendered on the WPS preview: each cell ships
  // a `focusField` slug derived from its visible label (Contractor →
  // `contractor`, "CRC Weld file No." → `crc-weld-file-no`, etc.).
  // The map below pairs each supported slug with the ref of its input
  // section. Unmapped slugs fall through to a scroll-to-top fallback
  // so the user still lands somewhere sensible after the click.
  const returnTo = searchParams.get('returnTo')
  const equipmentSectionRef = useRef<HTMLDivElement | null>(null)
  const projectNameSectionRef = useRef<HTMLDivElement | null>(null)
  const projectNumberSectionRef = useRef<HTMLDivElement | null>(null)
  const contractorSectionRef = useRef<HTMLDivElement | null>(null)
  const applicationSectionRef = useRef<HTMLDivElement | null>(null)
  const weldingCodeSectionRef = useRef<HTMLDivElement | null>(null)
  const clientSpecSectionRef = useRef<HTMLDivElement | null>(null)
  useFieldFocusHandler({
    ready: true,
    resolveTarget: (params) => {
      switch (params.focusField) {
        case 'equipment':
          return equipmentSectionRef.current
        case 'project':
        case 'project-name':
          return projectNameSectionRef.current
        case 'crc-weld-file-no':
        case 'project-number':
          return projectNumberSectionRef.current
        case 'contractor':
          return contractorSectionRef.current
        case 'application':
          return applicationSectionRef.current
        case 'welding-code-standard':
        case 'welding-code':
          return weldingCodeSectionRef.current
        case 'client-welding-spec':
        case 'client-spec':
          return clientSpecSectionRef.current
        default:
          return null
      }
    }
  })

  const contractorOptions = useMemo(
    () => getLabelsMerged('project_contractor', projectDetails.contractors),
    [getLabelsMerged, projectDetails.contractors]
  )
  const endClientOptions = useMemo(
    () => getLabelsMerged('project_end_client', projectDetails.endClients),
    [getLabelsMerged, projectDetails.endClients]
  )
  const applicationOptions = useMemo(
    () => getLabelsMerged('project_application', projectDetails.applications),
    [getLabelsMerged, projectDetails.applications]
  )
  const weldingCodeOptions = useMemo(
    () => getLabelsMerged('project_welding_code', projectDetails.weldingCodes),
    [getLabelsMerged, projectDetails.weldingCodes]
  )
  const clientSpecOptions = useMemo(() => {
    if (!id) {
      const set = new Set<string>()
      projectDetails.clientWeldingSpecs.forEach((s) => {
        const t = s.trim()
        if (t) set.add(t)
      })
      return Array.from(set).sort((a, b) => a.localeCompare(b))
    }
    return getLabelsMerged('project_client_spec', projectDetails.clientWeldingSpecs)
  }, [id, getLabelsMerged, projectDetails.clientWeldingSpecs])
  const equipmentOptions = useMemo(
    () => getLabelsMerged('project_equipment', projectDetails.equipment),
    [getLabelsMerged, projectDetails.equipment]
  )
  const materialSpecOptions = useMemo(
    () =>
      getLabelsMerged(
        'base_material_spec',
        pipeGroups.flatMap((g) => g.materials.map((m) => m.materialSpec))
      ),
    [getLabelsMerged, pipeGroups]
  )
  const gradeOptions = useMemo(
    () =>
      getLabelsMerged(
        'base_type_grade',
        pipeGroups.flatMap((g) => g.materials.map((m) => m.typeGrade))
      ),
    [getLabelsMerged, pipeGroups]
  )
  const bevelOptions = useMemo(
    () => getLabelsMerged('bevel_joint_type', pipeGroups.map((g) => g.bevelType).filter(Boolean)),
    [getLabelsMerged, pipeGroups]
  )
  const backingRequirementOptions = useMemo(
    () =>
      getLabelsMerged(
        'backing_requirement',
        pipeGroups.map((g) => g.backingRequirement).filter(Boolean)
      ),
    [getLabelsMerged, pipeGroups]
  )
  const backingMaterialOptions = useMemo(
    () =>
      getLabelsMerged(
        'backing_material',
        pipeGroups.map((g) => g.backingMaterial).filter(Boolean)
      ),
    [getLabelsMerged, pipeGroups]
  )
  const isDirty = useMemo(() => {
    if (!savedWorkspaceJson) return false
    return JSON.stringify({ projectDetails, pipeGroups }) !== savedWorkspaceJson
  }, [projectDetails, pipeGroups, savedWorkspaceJson])

  const materialSourceOptions = useMemo(
    () =>
      getLabelsMerged(
        'base_source_mill',
        pipeGroups.flatMap((g) => g.materials.map((m) => m.source))
      ),
    [getLabelsMerged, pipeGroups]
  )

  useEffect(() => {
    if (!id) return
    let cancelled = false
    setEditFetchLoading(true)
    fetchProjectById(id)
      .then((project) => {
        if (!cancelled) setFetchedProject(project)
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setEditFetchLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [id, fetchProjectById])

  useEffect(() => {
    if (!projectToEdit || editFetchLoading) return
    const nextDetails = {
      name: projectToEdit.name,
      projectNumber: projectToEdit.projectNumber ?? '',
      contractors: projectToEdit.contractors ?? (projectToEdit.client ? [projectToEdit.client] : []),
      endClients: projectToEdit.endClients ?? (projectToEdit.client ? [projectToEdit.client] : []),
      applications: projectToEdit.applications ?? (projectToEdit.application ? [projectToEdit.application] : []),
      weldingCodes: projectToEdit.weldingCodes ?? [],
      clientWeldingSpecs: projectToEdit.clientWeldingSpecs ?? [],
      equipment: projectToEdit.equipment ?? [],
      location: projectToEdit.location ?? ''
    }
    const mappedPipeSets = projectToEdit.pipeSets.length
      ? projectToEdit.pipeSets.map((pipe) => ({
          id: pipe.id,
          nominalOd: pipe.nominalOd,
          nominalOdUnit: pipe.nominalOdUnit,
          nominalThickness: pipe.wallThickness,
          nominalThicknessUnit: pipe.wallThicknessUnit,
          nominalThicknessRangeMin: '',
          nominalThicknessRangeMax: '',
          source: '',
          bevelType: '',
          backingRequirement: '',
          backingMaterial: '',
          materials: [emptyMaterial()]
        }))
      : [emptyPipeGroup()]
    setProjectDetails(nextDetails)
    setPipeGroups(mappedPipeSets)
    setExpandedGroups(mappedPipeSets.map((pipe) => pipe.id))
    setPipeSectionState(
      mappedPipeSets.reduce(
        (acc, pipe) => ({
          ...acc,
          [pipe.id]: { metadata: true, bevel: false, materials: false }
        }),
        {} as Record<string, { metadata: boolean; bevel: boolean; materials: boolean }>
      )
    )
    setPipeSetCountInput(String(mappedPipeSets.length))
    setSavedWorkspaceJson(JSON.stringify({ projectDetails: nextDetails, pipeGroups: mappedPipeSets }))
  }, [projectToEdit, getProject, editFetchLoading])

  const createBaselineDone = useRef(false)
  useEffect(() => {
    if (projectToEdit || createBaselineDone.current) return
    createBaselineDone.current = true
    setSavedWorkspaceJson(JSON.stringify({ projectDetails, pipeGroups }))
  }, [projectToEdit, projectDetails, pipeGroups])

  const addPipeGroup = () => {
    const newGroup = emptyPipeGroup()
    setPipeGroups((prev) => [...prev, newGroup])
    setExpandedGroups((prev) => [...prev, newGroup.id])
    setPipeSectionState((prev) => ({
      ...prev,
      [newGroup.id]: { metadata: true, bevel: false, materials: false }
    }))
    setPipeSetCountInput((prev) => {
      const next = Number(prev || 0) + 1
      return String(next)
    })
  }

  const removePipeGroup = (id: string) => {
    setPipeSetCountError(null)
    setPipeGroups((prev) => {
      if (prev.length <= 1) return prev
      const next = prev.filter((group) => group.id !== id)
      setExpandedGroups((ex) => ex.filter((groupId) => groupId !== id))
      setPipeSectionState((ps) => {
        const out = { ...ps }
        delete out[id]
        return out
      })
      setPipeSetCountInput(String(next.length))
      return next
    })
  }

  const updatePipeGroup = (id: string, field: keyof PipeSizeGroup, value: string) => {
    setPipeGroups((prev) =>
      prev.map((group) => (group.id === id ? { ...group, [field]: value } : group))
    )
  }

  const addMaterial = (groupId: string) => {
    setPipeGroups((prev) =>
      prev.map((group) =>
        group.id === groupId
          ? { ...group, materials: [...group.materials, emptyMaterial()] }
          : group
      )
    )
  }

  const removeMaterial = (groupId: string, materialId: string) => {
    setPipeGroups((prev) =>
      prev.map((group) =>
        group.id === groupId
          ? { ...group, materials: group.materials.filter((mat) => mat.id !== materialId) }
          : group
      )
    )
  }

  const updateMaterial = (
    groupId: string,
    materialId: string,
    field: keyof MaterialRow,
    value: string
  ) => {
    setPipeGroups((prev) =>
      prev.map((group) =>
        group.id === groupId
          ? {
              ...group,
              materials: group.materials.map((mat) =>
                mat.id === materialId ? { ...mat, [field]: value } : mat
              )
            }
          : group
      )
    )
  }

  const toggleGroup = (id: string) => {
    setExpandedGroups((prev) =>
      prev.includes(id) ? prev.filter((groupId) => groupId !== id) : [...prev, id]
    )
  }

  const expandAll = () => setExpandedGroups(pipeGroups.map((group) => group.id))
  const collapseAll = () => setExpandedGroups([])

  const openPipeSection = (groupId: string, section: 'metadata' | 'bevel' | 'materials') => {
    setPipeSectionState((prev) => ({
      ...prev,
      [groupId]: {
        metadata: section === 'metadata',
        bevel: section === 'bevel',
        materials: section === 'materials'
      }
    }))
  }

  const getPipeSectionState = (groupId: string) =>
    pipeSectionState[groupId] ?? { metadata: true, bevel: false, materials: false }

  const summarizeGroup = (group: PipeSizeGroup) => {
    const od = group.nominalOd ? `OD ${group.nominalOd} ${group.nominalOdUnit}` : 'OD TBD'
    const wt = group.nominalThickness ? `WT ${group.nominalThickness} ${group.nominalThicknessUnit}` : 'WT TBD'
    return `${od} • ${wt}`
  }

  const getError = (key: string) => (attemptedSave ? errors[key] : undefined)

  const errorSummary = () => {
    const keys = Object.keys(errors)
    if (keys.length === 0) {
      return null
    }
    let projectCount = 0
    let pipeCount = 0
    keys.forEach((key) => {
      if (key.startsWith('project.')) {
        projectCount += 1
      } else if (key.startsWith('pipe.')) {
        pipeCount += 1
      }
    })
    return {
      total: keys.length,
      projectCount,
      pipeCount
    }
  }

  const summary = attemptedSave ? errorSummary() : null

  const pipeGroupOccupied = (group: PipeSizeGroup) => {
    if (
      group.nominalOd.trim() ||
      group.nominalThickness.trim() ||
      group.nominalThicknessRangeMin.trim() ||
      group.nominalThicknessRangeMax.trim() ||
      group.source.trim() ||
      group.bevelType.trim() ||
      group.backingRequirement.trim() ||
      group.backingMaterial.trim()
    ) {
      return true
    }
    return group.materials.some(
      (m) => m.materialSpec.trim() || m.typeGrade.trim() || m.manufacture.trim() || m.source.trim()
    )
  }

  const applyPipeSetTargetCount = useCallback((rawCount: number) => {
    const nextCount = Math.max(1, rawCount)
    setPipeSetCountError(null)
    setPipeGroups((prev) => {
      const res = resizeArrayByCount(prev, nextCount, emptyPipeGroup, (g) => pipeGroupOccupied(g), {
        blockedShrinkMessage: pipeSetBlockedMessage
      })
      if (!res.ok) {
        setPipeSetCountError(res.error)
        return prev
      }
      const next = res.items
      if (next.length !== prev.length) {
        setExpandedGroups((expanded) => {
          if (next.length < prev.length) {
            return expanded.filter((id) => next.some((g) => g.id === id))
          }
          const added = next.filter((g) => !prev.some((p) => p.id === g.id)).map((g) => g.id)
          return [...expanded, ...added]
        })
        setPipeSectionState((sections) =>
          next.reduce(
            (acc, group) => ({
              ...acc,
              [group.id]: sections[group.id] ?? { metadata: true, bevel: false, materials: false }
            }),
            {} as Record<string, { metadata: boolean; bevel: boolean; materials: boolean }>
          )
        )
      }
      return next
    })
  }, [])

  useEffect(() => {
    if (pipeSetCountError) return
    setPipeSetCountInput(String(pipeGroups.length))
  }, [pipeGroups.length, pipeSetCountError])

  useEffect(() => {
    if (!pipeSetCountError) return
    const onPointerDown = (e: PointerEvent) => {
      if (pipeSetCountInputRef.current?.contains(e.target as Node)) return
      setPipeSetCountError(null)
      setPipeSetCountInput(String(pipeGroups.length))
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => document.removeEventListener('pointerdown', onPointerDown, true)
  }, [pipeSetCountError, pipeGroups.length])

  const validate = () => {
    const nextErrors: Record<string, string> = {}

    if (!projectDetails.name.trim()) nextErrors['project.name'] = 'Required'
    if (isData360Create && !projectDetails.location?.trim()) {
      nextErrors['project.location'] = 'Required'
    }
    if (projectDetails.contractors.length === 0) nextErrors['project.contractors'] = 'Required'
    if (projectDetails.endClients.length === 0) nextErrors['project.endClients'] = 'Required'
    if (projectDetails.applications.length === 0) nextErrors['project.applications'] = 'Required'
    if (projectDetails.weldingCodes.length === 0) nextErrors['project.weldingCodes'] = 'Required'
    if (projectDetails.clientWeldingSpecs.length === 0) nextErrors['project.clientWeldingSpecs'] = 'Required'
    if (projectDetails.equipment.length === 0) nextErrors['project.equipment'] = 'Required'

    pipeGroups.forEach((group) => {
      const prefix = `pipe.${group.id}`
      const odValue = Number(group.nominalOd)
      const wtValue = Number(group.nominalThickness)

      if (!group.nominalOd) nextErrors[`${prefix}.nominalOd`] = 'Required'
      else if (Number.isNaN(odValue) || odValue <= 0) nextErrors[`${prefix}.nominalOd`] = 'Enter a valid value'

      if (!group.nominalThickness) nextErrors[`${prefix}.nominalThickness`] = 'Required'
      else if (Number.isNaN(wtValue) || wtValue <= 0) nextErrors[`${prefix}.nominalThickness`] = 'Enter a valid value'

      const rangeMin = group.nominalThicknessRangeMin
      const rangeMax = group.nominalThicknessRangeMax
      if (rangeMin || rangeMax) {
        const minValue = Number(rangeMin)
        const maxValue = Number(rangeMax)
        if (!rangeMin || Number.isNaN(minValue)) nextErrors[`${prefix}.rangeMin`] = 'Enter min'
        if (!rangeMax || Number.isNaN(maxValue)) nextErrors[`${prefix}.rangeMax`] = 'Enter max'
        if (!Number.isNaN(minValue) && !Number.isNaN(maxValue) && minValue > maxValue) {
          nextErrors[`${prefix}.rangeMax`] = 'Max must be >= min'
        }
        if (!Number.isNaN(minValue) && !Number.isNaN(maxValue) && wtValue) {
          if (wtValue < minValue || wtValue > maxValue) {
            nextErrors[`${prefix}.nominalThickness`] = 'Nominal should fall within range'
          }
        }
      }

    })

    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleSave = async () => {
    if (deleteBusy || saveBusy) return
    setAttemptedSave(true)
    if (!isLegacyEdit && !validate()) return

    if (isData360Create) {
      setSaveBusy(true)
      try {
        const totalPipeSizes = pipeGroups.length
        const result = await createData360Project({
          project: {
            name: projectDetails.name.trim(),
            projectNumber: projectDetails.projectNumber.trim() || projectDetails.name.trim(),
            location: projectDetails.location?.trim(),
            contractors: projectDetails.contractors,
            endClients: projectDetails.endClients,
            applications: projectDetails.applications,
            weldingCodes: projectDetails.weldingCodes,
            clientWeldingSpecs: projectDetails.clientWeldingSpecs,
            equipment: projectDetails.equipment,
            dueDate: 'TBD',
            pipeSizeRange:
              totalPipeSizes === 1 ? '1 pipe size set' : `${totalPipeSizes} pipe size sets`,
            targetWpsCount: totalPipeSizes * 2,
            status: 'Active'
          },
          pipes: pipeGroups.map((group) => ({
            id: group.id,
            nominalOd: group.nominalOd,
            nominalOdUnit: group.nominalOdUnit,
            wallThickness: group.nominalThickness,
            wallThicknessUnit: group.nominalThicknessUnit,
            source: group.source,
            nominalThicknessRangeMin: group.nominalThicknessRangeMin,
            nominalThicknessRangeMax: group.nominalThicknessRangeMax,
            bevelType: group.bevelType,
            backingRequirement: group.backingRequirement,
            backingMaterial: group.backingMaterial,
            materials: group.materials
          }))
        })
        if (result.project) {
          patchProjectInList(result.project)
        } else {
          await refreshProjects()
        }
        navigate(`/projects/${result.shadowProjectId}/next`)
      } catch (err: any) {
        toast.error(formatData360DisplayText(err?.message || `Failed to create ${DATA_360_DISPLAY_NAME} project.`))
      } finally {
        setSaveBusy(false)
      }
      return
    }

    const pipeSetsForSave = pipeGroups.map((group) => ({
          id: group.id,
          nominalOd: group.nominalOd,
          nominalOdUnit: group.nominalOdUnit,
          wallThickness: group.nominalThickness,
          wallThicknessUnit: group.nominalThicknessUnit
        }))

    const totalPipeSizes = pipeSetsForSave.length
    const baseProject: ProjectDetails = {
      id: projectToEdit?.id ?? `proj-${Math.random().toString(36).slice(2, 8)}`,
      name: projectDetails.name.trim(),
      client:
        projectDetails.endClients[0] ??
        projectDetails.contractors[0] ??
        projectToEdit?.client ??
        '',
      application: projectDetails.applications[0] ?? projectToEdit?.application ?? '',
      location: projectDetails.location?.trim() ?? '',
      projectNumber: projectDetails.projectNumber.trim(),
      status: 'Active',
      pipeSizeRange: totalPipeSizes === 1 ? '1 pipe size set' : `${totalPipeSizes} pipe size sets`,
      targetWpsCount: totalPipeSizes * 2,
      dueDate: 'TBD',
      contractors: projectDetails.contractors,
      endClients: projectDetails.endClients,
      applications: projectDetails.applications,
      weldingCodes: projectDetails.weldingCodes,
      clientWeldingSpecs: projectDetails.clientWeldingSpecs,
      equipment: projectDetails.equipment,
      pipeSets: pipeSetsForSave,
      data360ProjectId: projectToEdit?.data360ProjectId ?? null
    }

    setSaveBusy(true)
    try {
      if (!projectToEdit) {
        return
      }
      await updateProject(baseProject)

      setSavedWorkspaceJson(JSON.stringify({ projectDetails, pipeGroups }))
      navigate(`/projects/${baseProject.id}/next`)
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save project.')
    } finally {
      setSaveBusy(false)
    }
  }

  const handleDeleteProject = async () => {
    if (!projectToEdit || deleteBusy) return
    setDeleteBusy(true)
    try {
      await deleteProject(projectToEdit.id)
      toast.success('Project deleted.')
      setDeleteDialogOpen(false)
      navigate('/resources')
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete project.')
    } finally {
      setDeleteBusy(false)
    }
  }

  if (saveBusy) {
    return <PageLoader label="Saving project…" />
  }

  if (id && (projectsLoading || editFetchLoading)) {
    return <PageLoader label="Loading project…" />
  }

  if (id && !projectsLoading && !editFetchLoading && !projectToEdit) {
    return (
      <Card title="Project not found">
        <p className="text-sm text-gray-500">Return to the Project Hub to select a project.</p>
        <div className="mt-4">
          <Button type="button" onClick={() => navigate('/resources')}>
            Go to Project Hub
          </Button>
        </div>
      </Card>
    )
  }

  const pageHeaderBackTo = returnTo
    ? { to: returnTo, label: backToLabelFor(returnTo) }
    : projectToEdit
      ? { to: `/projects/${projectToEdit.id}/next`, label: 'Back to project' }
      : { to: '/resources', label: 'Back to Project Hub' }

  return (
    <div className="space-y-6">
      <EditingReturnBanner returnTo={returnTo} contextLabel="Equipment" />
      <PageHeader
        backTo={pageHeaderBackTo}
        title={projectToEdit ? 'Edit Project' : 'Create Project'}
        subtitle={<span>Capture project details and pipe sets.</span>}
        actions={
          <>
            {projectToEdit && canDeleteProject && (
              <Button
                variant="ghost"
                className="justify-center text-error-600 hover:bg-error-50"
                type="button"
                onClick={() => setDeleteDialogOpen(true)}
                disabled={deleteBusy || saveBusy}
              >
                <TrashIcon className="h-4 w-4 mr-2" />
                Delete Project
              </Button>
            )}
            <Button
              className="justify-center"
              type="button"
              disabled={!isDirty || deleteBusy || saveBusy}
              onClick={handleSave}
            >
              <PlusCircleIcon className="h-4 w-4 mr-2" />
              {projectToEdit ? 'Save Changes' : 'Save Project'}
            </Button>
          </>
        }
      />
      {pipeSetCountError && <p className="text-xs text-error-600">{pipeSetCountError}</p>}
      {summary && (
        <div className="rounded-lg border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700">
          <p className="font-semibold">Some required fields are missing.</p>
          <p className="text-xs text-error-600">
            Please complete the fields highlighted in red before saving.
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs text-error-700">
            {summary.projectCount > 0 && (
              <span className="rounded-full bg-white/70 px-2 py-1">
                Project details: {summary.projectCount}
              </span>
            )}
            {summary.pipeCount > 0 && (
              <span className="rounded-full bg-white/70 px-2 py-1">
                Pipe size sets: {summary.pipeCount}
              </span>
            )}
          </div>
        </div>
      )}
      <div className="min-w-0 space-y-6 border-0 p-0">
      {activeSection === 'project' && (
      <Card title="Project details">
        {/* {isData360Create ? (
          <p className="mb-4 text-xs text-gray-500">
            Creates a new project in Data360 with WPS metadata stored in extraDetails.
          </p>
        ) : null} */}
        {isLegacyEdit ? (
          <p className="mb-4 text-xs text-gray-500">
            Legacy project — stored in WPS Studio. New projects are created in {DATA_360_DISPLAY_NAME}.
          </p>
        ) : null}
        {isD360Owned ? (
          <p className="mb-4 text-xs text-gray-500">
            Core project fields are managed in {DATA_360_DISPLAY_NAME}. Edit WPS-specific metadata below.
          </p>
        ) : null}
        {projectToEdit?.data360ProjectId && projectToEdit?.createdFromApp === 'WPS-Studio' ? (
          <p className="mb-4 text-xs text-gray-500">
            This project is synced with {DATA_360_DISPLAY_NAME}. Core fields and WPS metadata can be edited here.
          </p>
        ) : null}
        <div className="grid gap-4 md:grid-cols-2">
            <div ref={projectNameSectionRef} tabIndex={-1}>
              <label className="text-xs font-medium text-gray-500">
                Project Name <span className="text-error-500">*</span>
              </label>
              <input
                className={clsx(
                  'input-primary mt-1',
                  coreFieldsReadOnly &&
                    'cursor-not-allowed bg-page-bg text-heritage-grey opacity-70',
                  getError('project.name') &&
                    'border-error-500 focus:border-error-500 focus:ring-error-500'
                )}
                placeholder="North Ridge 36-in Pipeline"
                value={projectDetails.name}
                readOnly={coreFieldsReadOnly}
                {...data360LockedDomProps}
                onChange={(event) =>
                  setProjectDetails((prev) => ({ ...prev, name: event.target.value }))
                }
              />
              {getError('project.name') && (
                <p className="mt-1 text-xs text-error-600">{getError('project.name')}</p>
              )}
            </div>
            <div ref={projectNumberSectionRef} tabIndex={-1}>
              <label className="text-xs font-medium text-gray-500">
                Project Number
              </label>
              <input
                className={clsx(
                  'input-primary mt-1',
                  coreFieldsReadOnly &&
                    'cursor-not-allowed bg-page-bg text-heritage-grey opacity-70'
                )}
                placeholder="PRJ-2026-004"
                value={projectDetails.projectNumber}
                readOnly={coreFieldsReadOnly}
                {...data360LockedDomProps}
                onChange={(event) =>
                  setProjectDetails((prev) => ({ ...prev, projectNumber: event.target.value }))
                }
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">
                Location {isData360Create ? <span className="text-error-500">*</span> : null}
              </label>
              <input
                className={clsx(
                  'input-primary mt-1',
                  coreFieldsReadOnly &&
                    'cursor-not-allowed bg-page-bg text-heritage-grey opacity-70',
                  getError('project.location') &&
                    'border-error-500 focus:border-error-500 focus:ring-error-500'
                )}
                placeholder="Houston, TX"
                value={projectDetails.location}
                readOnly={coreFieldsReadOnly}
                {...data360LockedDomProps}
                onChange={(event) =>
                  setProjectDetails((prev) => ({ ...prev, location: event.target.value }))
                }
              />
              {getError('project.location') && (
                <p className="mt-1 text-xs text-error-600">{getError('project.location')}</p>
              )}
            </div>
            <div ref={contractorSectionRef} tabIndex={-1}>
              <label className="text-xs font-medium text-gray-500">
                Contractor Name <span className="text-error-500">*</span>
              </label>
              <MultiSelect
                values={projectDetails.contractors}
                options={contractorOptions}
                placeholder="Select or enter contractor name"
                addLabel="Add New Contractor"
                createModalTitle="Add New Contractor"
                createFieldLabel="Contractor Name"
                createInputPlaceholder="Enter contractor name"
                searchPlaceholder="Search contractors..."
                triggerIcon={<BuildingOffice2Icon className="h-5 w-5" />}
                interactionLocked={coreFieldsReadOnly}
                lockedHint={`Managed in ${DATA_360_DISPLAY_NAME}`}
                onChange={(values) =>
                  setProjectDetails((prev) => ({ ...prev, contractors: values }))
                }
                onCreateOption={async (value) => {
                  await addLabel('project_contractor', value)
                  setProjectDetails((prev) =>
                    prev.contractors.includes(value)
                      ? prev
                      : { ...prev, contractors: [...prev.contractors, value] }
                  )
                }}
                className="mt-1"
                inputClassName={clsx(
                  getError('project.contractors') &&
                    'border-error-500 focus:border-error-500 focus:ring-error-500'
                )}
              />
              {getError('project.contractors') && (
                <p className="mt-1 text-xs text-error-600">{getError('project.contractors')}</p>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">
                End Client <span className="text-error-500">*</span>
              </label>
              <MultiSelect
                values={projectDetails.endClients}
                options={endClientOptions}
                placeholder="Select end clients..."
                addLabel="Add End Clients"
                interactionLocked={coreFieldsReadOnly}
                lockedHint={`Managed in ${DATA_360_DISPLAY_NAME}`}
                onChange={(values) =>
                  setProjectDetails((prev) => ({ ...prev, endClients: values }))
                }
                onCreateOption={async (value) => {
                  await addLabel('project_end_client', value)
                  setProjectDetails((prev) =>
                    prev.endClients.includes(value)
                      ? prev
                      : { ...prev, endClients: [...prev.endClients, value] }
                  )
                }}
                className="mt-1"
                inputClassName={clsx(
                  getError('project.endClients') &&
                    'border-error-500 focus:border-error-500 focus:ring-error-500'
                )}
              />
              {getError('project.endClients') && (
                <p className="mt-1 text-xs text-error-600">{getError('project.endClients')}</p>
              )}
            </div>
            <div ref={applicationSectionRef} tabIndex={-1}>
              <label className="text-xs font-medium text-gray-500">
                Application <span className="text-error-500">*</span>
              </label>
              <MultiSelect
                values={projectDetails.applications}
                options={applicationOptions}
                placeholder="Select applications..."
                addLabel="Add Applications"
                onChange={(values) =>
                  setProjectDetails((prev) => ({ ...prev, applications: values }))
                }
                onCreateOption={async (value) => {
                  await addLabel('project_application', value)
                  setProjectDetails((prev) =>
                    prev.applications.includes(value)
                      ? prev
                      : { ...prev, applications: [...prev.applications, value] }
                  )
                }}
                className="mt-1"
                inputClassName={clsx(
                  getError('project.applications') &&
                    'border-error-500 focus:border-error-500 focus:ring-error-500'
                )}
              />
              {getError('project.applications') && (
                <p className="mt-1 text-xs text-error-600">{getError('project.applications')}</p>
              )}
            </div>
            <div ref={weldingCodeSectionRef} tabIndex={-1}>
              <label className="text-xs font-medium text-gray-500">
                Welding Code / Standard <span className="text-error-500">*</span>
              </label>
              <MultiSelect
                values={projectDetails.weldingCodes}
                options={weldingCodeOptions}
                placeholder="Select welding codes..."
                addLabel="Add Welding Codes"
                onChange={(values) =>
                  setProjectDetails((prev) => ({ ...prev, weldingCodes: values }))
                }
                onCreateOption={async (value) => {
                  await addLabel('project_welding_code', value)
                  setProjectDetails((prev) =>
                    prev.weldingCodes.includes(value)
                      ? prev
                      : { ...prev, weldingCodes: [...prev.weldingCodes, value] }
                  )
                }}
                className="mt-1"
                inputClassName={clsx(
                  getError('project.weldingCodes') &&
                    'border-error-500 focus:border-error-500 focus:ring-error-500'
                )}
              />
              {getError('project.weldingCodes') && (
                <p className="mt-1 text-xs text-error-600">{getError('project.weldingCodes')}</p>
              )}
            </div>
            <div ref={clientSpecSectionRef} tabIndex={-1}>
              <label className="text-xs font-medium text-gray-500">
                Client Welding Spec <span className="text-error-500">*</span>
              </label>
              <MultiSelect
                values={projectDetails.clientWeldingSpecs}
                options={clientSpecOptions}
                placeholder="Select client specs..."
                addLabel="Add Client Specs"
                onChange={(values) =>
                  setProjectDetails((prev) => ({ ...prev, clientWeldingSpecs: values }))
                }
                onCreateOption={async (value) => {
                  if (id) {
                    await addLabel('project_client_spec', value, id)
                  } else {
                    toast.message('Save the project first to add this spec to the catalog for reuse.')
                  }
                  setProjectDetails((prev) =>
                    prev.clientWeldingSpecs.includes(value)
                      ? prev
                      : { ...prev, clientWeldingSpecs: [...prev.clientWeldingSpecs, value] }
                  )
                }}
                className="mt-1"
                inputClassName={clsx(
                  getError('project.clientWeldingSpecs') &&
                    'border-error-500 focus:border-error-500 focus:ring-error-500'
                )}
              />
              {getError('project.clientWeldingSpecs') && (
                <p className="mt-1 text-xs text-error-600">
                  {getError('project.clientWeldingSpecs')}
                </p>
              )}
            </div>
            <div ref={equipmentSectionRef} data-focus-field="equipment">
              <label className="text-xs font-medium text-gray-500">
                Equipment <span className="text-error-500">*</span>
              </label>
              <MultiSelect
                values={projectDetails.equipment}
                options={equipmentOptions}
                placeholder="Select equipment..."
                addLabel="Add Equipment"
                onChange={(values) =>
                  setProjectDetails((prev) => ({ ...prev, equipment: values }))
                }
                onCreateOption={async (value) => {
                  await addLabel('project_equipment', value)
                  setProjectDetails((prev) =>
                    prev.equipment.includes(value)
                      ? prev
                      : { ...prev, equipment: [...prev.equipment, value] }
                  )
                }}
                className="mt-1"
                inputClassName={clsx(
                  getError('project.equipment') &&
                    'border-error-500 focus:border-error-500 focus:ring-error-500'
                )}
              />
              {getError('project.equipment') && (
                <p className="mt-1 text-xs text-error-600">{getError('project.equipment')}</p>
              )}
            </div>
          </div>
          <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 px-4 py-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-gray-900">Pipe size sets (quick start)</p>
                <p className="text-xs text-gray-500">
                  Set the count and capture OD + wall thickness up front.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  ref={pipeSetCountInputRef}
                  className="input-primary w-20 text-center"
                  type="number"
                  min={1}
                  value={pipeSetCountInput}
                  onChange={(event) => {
                    const raw = event.target.value
                    setPipeSetCountInput(raw)
                    if (raw.trim() === '') return
                    applyPipeSetTargetCount(parseInt(raw, 10) || 0)
                  }}
                  onBlur={() => {
                    setPipeSetCountError(null)
                    setPipeSetCountInput(String(pipeGroups.length))
                  }}
                />
              </div>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {pipeGroups.map((group, index) => (
                <div key={group.id} className="rounded-lg border border-gray-200 bg-white px-3 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-gray-700">Pipe Set {index + 1}</p>
                    <div className="flex items-center gap-1">
                      <span className="text-[11px] text-gray-400">OD + WT</span>
                      {pipeGroups.length > 1 && (
                        <button
                          type="button"
                          className="rounded p-1 text-error-600 transition hover:bg-error-50"
                          title="Remove this pipe size set"
                          aria-label={`Remove quick start pipe set ${index + 1}`}
                          onClick={() => removePipeGroup(group.id)}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 space-y-2">
                    <div>
                      <label className="text-[11px] font-medium text-gray-500">Nominal OD</label>
                      <div
                        className={clsx(
                          'mt-1 flex rounded-md border shadow-sm focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500 divide-x divide-gray-200',
                          getError(`pipe.${group.id}.nominalOd`)
                            ? 'border-error-500 focus-within:ring-error-500 focus-within:border-error-500'
                            : 'border-gray-300'
                        )}
                      >
                        <input
                          className="w-full border-0 bg-transparent px-2 py-1.5 text-xs focus:outline-none"
                          placeholder="10.75"
                          type="number"
                          step="any"
                          value={group.nominalOd}
                          onChange={(event) =>
                            updatePipeGroup(group.id, 'nominalOd', event.target.value)
                          }
                        />
                        <InlineSelect
                          value={group.nominalOdUnit}
                          options={[
                            { value: 'in', label: 'in' },
                            { value: 'mm', label: 'mm' }
                          ]}
                          onChange={(nextValue) =>
                            updatePipeGroup(group.id, 'nominalOdUnit', nextValue)
                          }
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-[11px] font-medium text-gray-500">Nominal WT</label>
                      <div
                        className={clsx(
                          'mt-1 flex rounded-md border shadow-sm focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500 divide-x divide-gray-200',
                          getError(`pipe.${group.id}.nominalThickness`)
                            ? 'border-error-500 focus-within:ring-error-500 focus-within:border-error-500'
                            : 'border-gray-300'
                        )}
                      >
                        <input
                          className="w-full border-0 bg-transparent px-2 py-1.5 text-xs focus:outline-none"
                          placeholder="0.500"
                          type="number"
                          step="any"
                          value={group.nominalThickness}
                          onChange={(event) =>
                            updatePipeGroup(group.id, 'nominalThickness', event.target.value)
                          }
                        />
                        <InlineSelect
                          value={group.nominalThicknessUnit}
                          options={[
                            { value: 'mm', label: 'mm' },
                            { value: 'in', label: 'in' }
                          ]}
                          onChange={(nextValue) =>
                            updatePipeGroup(group.id, 'nominalThicknessUnit', nextValue)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {activeSection === 'pipes' && (
        <Card
          title="Pipe Sizes, Materials, and Bevel Requirements"
          action={
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                type="button"
                className="text-gray-600"
                onClick={() => setActiveSection('project')}
              >
                Back to project details
              </Button>
              <Button variant="secondary" type="button" onClick={addPipeGroup}>
                <PlusCircleIcon className="h-4 w-4 mr-2" />
                Add Pipe Size Set
              </Button>
              <Button type="button" onClick={handleSave}>
                {projectToEdit ? 'Save Changes' : 'Save Project'}
              </Button>
            </div>
          }
        >
          <div className="space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-gray-900">Define pipe size sets</p>
                <p className="text-xs text-gray-500">
                  Add each OD and WT combination, then expand to capture materials and bevel details.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" type="button" onClick={expandAll}>
                  Expand all
                </Button>
                <Button variant="secondary" type="button" onClick={collapseAll}>
                  Collapse all
                </Button>
              </div>
            </div>

          {pipeGroups.map((group, index) => {
            const isOpen = expandedGroups.includes(group.id)
            const sectionState = getPipeSectionState(group.id)
            const metadataComplete = Boolean(
              group.nominalOd && group.nominalThickness
            )
            const bevelComplete = Boolean(
              group.bevelType &&
                group.backingRequirement &&
                (group.backingRequirement !== 'Required' || group.backingMaterial)
            )
            const materialsComplete = group.materials.every(
              (material) =>
                material.materialSpec &&
                material.typeGrade &&
                material.manufacture &&
                material.source
            )
            return (
              <div key={group.id} className="rounded-lg border border-gray-200">
                <div className="flex w-full flex-wrap items-center gap-2 px-4 py-3">
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.id)}
                    className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-3 text-left"
                  >
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Pipe Size Set {index + 1}</p>
                      <p className="text-xs text-gray-500">{summarizeGroup(group)}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge tone={isOpen ? 'primary' : 'gray'}>{isOpen ? 'Open' : 'Closed'}</Badge>
                      <ChevronDownIcon
                        className={`h-5 w-5 text-gray-500 transition-transform ${
                          isOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </div>
                  </button>
                  <button
                    type="button"
                    className={clsx(
                      'inline-flex shrink-0 items-center rounded-md p-2 text-error-600 transition hover:bg-error-50',
                      pipeGroups.length <= 1 && 'cursor-not-allowed opacity-40'
                    )}
                    disabled={pipeGroups.length <= 1}
                    title={
                      pipeGroups.length <= 1
                        ? 'At least one pipe size set is required.'
                        : 'Remove this pipe size set'
                    }
                    aria-label={`Remove pipe size set ${index + 1}`}
                    onClick={() => removePipeGroup(group.id)}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>

                {isOpen && (
                  <div className="border-t border-gray-200 px-4 py-4 space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-gray-900">Pipe size details</p>
                      <Button
                        variant="ghost"
                        className="text-error-600 hover:bg-error-50"
                        type="button"
                        disabled={pipeGroups.length <= 1}
                        title={pipeGroups.length <= 1 ? 'At least one pipe size set is required.' : undefined}
                        onClick={() => removePipeGroup(group.id)}
                      >
                        <TrashIcon className="h-4 w-4 mr-2" />
                        Remove
                      </Button>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => openPipeSection(group.id, 'metadata')}
                        className={clsx(
                          'rounded-full px-3 py-1 text-xs font-medium transition',
                          sectionState.metadata
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:text-gray-900'
                        )}
                      >
                        Metadata
                      </button>
                      <button
                        type="button"
                        onClick={() => openPipeSection(group.id, 'bevel')}
                        className={clsx(
                          'rounded-full px-3 py-1 text-xs font-medium transition',
                          sectionState.bevel
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:text-gray-900'
                        )}
                      >
                        Bevel + Backing
                      </button>
                      <button
                        type="button"
                        onClick={() => openPipeSection(group.id, 'materials')}
                        className={clsx(
                          'rounded-full px-3 py-1 text-xs font-medium transition',
                          sectionState.materials
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:text-gray-900'
                        )}
                      >
                        Materials
                      </button>
                      <Badge tone={metadataComplete ? 'success' : 'gray'}>
                        Metadata {metadataComplete ? 'Ready' : 'Missing'}
                      </Badge>
                      <Badge tone={bevelComplete ? 'success' : 'gray'}>
                        Bevel {bevelComplete ? 'Ready' : 'Missing'}
                      </Badge>
                      <Badge tone={materialsComplete ? 'success' : 'gray'}>
                        Materials {materialsComplete ? 'Ready' : 'Missing'}
                      </Badge>
                    </div>

                    {sectionState.metadata && (
                      <div className="rounded-lg border border-gray-200 bg-white p-4">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-gray-900">Pipe metadata</p>
                          <Badge tone={metadataComplete ? 'success' : 'warning'}>
                            {metadataComplete ? 'Complete' : 'Required'}
                          </Badge>
                        </div>
                        <div className="mt-3 grid gap-4 md:grid-cols-3">
                          <div>
                            <label className="text-xs font-medium text-gray-500">
                              Nominal OD <span className="text-error-500">*</span>
                            </label>
                            <div
                              className={clsx(
                                'mt-1 flex rounded-md border shadow-sm focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500 divide-x divide-gray-200',
                                getError(`pipe.${group.id}.nominalOd`)
                                  ? 'border-error-500 focus-within:ring-error-500 focus-within:border-error-500'
                                  : 'border-gray-300'
                              )}
                            >
                              <input
                                className="w-full border-0 bg-transparent px-3 py-2 text-sm focus:outline-none"
                                placeholder="10.75"
                                type="number"
                                step="any"
                                value={group.nominalOd}
                                onChange={(event) =>
                                  updatePipeGroup(group.id, 'nominalOd', event.target.value)
                                }
                              />
                              <InlineSelect
                                value={group.nominalOdUnit}
                                options={[
                                  { value: 'in', label: 'in' },
                                  { value: 'mm', label: 'mm' }
                                ]}
                                onChange={(nextValue) =>
                                  updatePipeGroup(group.id, 'nominalOdUnit', nextValue)
                                }
                              />
                            </div>
                            {getError(`pipe.${group.id}.nominalOd`) && (
                              <p className="mt-1 text-xs text-error-600">
                                {getError(`pipe.${group.id}.nominalOd`)}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-500">
                              Nominal Thickness (WT) <span className="text-error-500">*</span>
                            </label>
                            <div
                              className={clsx(
                                'mt-1 flex rounded-md border shadow-sm focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500 divide-x divide-gray-200',
                                getError(`pipe.${group.id}.nominalThickness`)
                                  ? 'border-error-500 focus-within:ring-error-500 focus-within:border-error-500'
                                  : 'border-gray-300'
                              )}
                            >
                              <input
                                className="w-full border-0 bg-transparent px-3 py-2 text-sm focus:outline-none"
                                placeholder="0.500"
                                type="number"
                                step="any"
                                value={group.nominalThickness}
                                onChange={(event) =>
                                  updatePipeGroup(group.id, 'nominalThickness', event.target.value)
                                }
                              />
                              <InlineSelect
                                value={group.nominalThicknessUnit}
                                options={[
                                  { value: 'mm', label: 'mm' },
                                  { value: 'in', label: 'in' }
                                ]}
                                onChange={(nextValue) =>
                                  updatePipeGroup(group.id, 'nominalThicknessUnit', nextValue)
                                }
                              />
                            </div>
                            {getError(`pipe.${group.id}.nominalThickness`) && (
                              <p className="mt-1 text-xs text-error-600">
                                {getError(`pipe.${group.id}.nominalThickness`)}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-500">
                              Source <span className="text-error-500">*</span>
                            </label>
                            <input
                              className={clsx(
                                'input-primary mt-1',
                                getError(`pipe.${group.id}.source`) &&
                                  'border-error-500 focus:border-error-500 focus:ring-error-500'
                              )}
                              placeholder="Mill A"
                              value={group.source}
                              onChange={(event) =>
                                updatePipeGroup(group.id, 'source', event.target.value)
                              }
                            />
                            {getError(`pipe.${group.id}.source`) && (
                              <p className="mt-1 text-xs text-error-600">
                                {getError(`pipe.${group.id}.source`)}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="mt-4">
                          <label className="text-xs font-medium text-gray-500">
                            Thickness Range (optional)
                          </label>
                          <div className="mt-1 grid gap-2 md:grid-cols-2">
                            <input
                              className={clsx(
                                'input-primary',
                                getError(`pipe.${group.id}.rangeMin`) &&
                                  'border-error-500 focus:border-error-500 focus:ring-error-500'
                              )}
                              placeholder="Min"
                              type="number"
                              step="any"
                              value={group.nominalThicknessRangeMin}
                              onChange={(event) =>
                                updatePipeGroup(
                                  group.id,
                                  'nominalThicknessRangeMin',
                                  event.target.value
                                )
                              }
                            />
                            <input
                              className={clsx(
                                'input-primary',
                                getError(`pipe.${group.id}.rangeMax`) &&
                                  'border-error-500 focus:border-error-500 focus:ring-error-500'
                              )}
                              placeholder="Max"
                              type="number"
                              step="any"
                              value={group.nominalThicknessRangeMax}
                              onChange={(event) =>
                                updatePipeGroup(
                                  group.id,
                                  'nominalThicknessRangeMax',
                                  event.target.value
                                )
                              }
                            />
                          </div>
                          {(getError(`pipe.${group.id}.rangeMin`) ||
                            getError(`pipe.${group.id}.rangeMax`)) && (
                            <p className="mt-1 text-xs text-error-600">
                              {getError(`pipe.${group.id}.rangeMin`) ||
                                getError(`pipe.${group.id}.rangeMax`)}
                            </p>
                          )}
                          <p className="mt-2 text-xs text-gray-400">
                            Example: 0.375 - 0.625 in (nominal 0.500 in).
                          </p>
                        </div>
                      </div>
                    )}

                    {sectionState.bevel && (
                      <div className="rounded-lg border border-gray-200 bg-white p-4">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-gray-900">Bevel geometry + backing</p>
                          <Badge tone={bevelComplete ? 'success' : 'warning'}>
                            {bevelComplete ? 'Complete' : 'Required'}
                          </Badge>
                        </div>
                        <div className="mt-3 grid gap-4 md:grid-cols-3">
                          <div>
                            <label className="text-xs font-medium text-gray-500">
                              Bevel Type <span className="text-error-500">*</span>
                            </label>
                            <Combobox
                              value={group.bevelType}
                              options={bevelOptions}
                              placeholder="Select or type"
                              onChange={(nextValue) =>
                                updatePipeGroup(group.id, 'bevelType', nextValue)
                              }
                              onCreateOption={async (value) => {
                                await addLabel('bevel_joint_type', value)
                              }}
                              className="mt-1"
                              inputClassName={clsx(
                                getError(`pipe.${group.id}.bevelType`) &&
                                  'border-error-500 focus:border-error-500 focus:ring-error-500'
                              )}
                            />
                            {getError(`pipe.${group.id}.bevelType`) && (
                              <p className="mt-1 text-xs text-error-600">Required</p>
                            )}
                          </div>
                          <div className="flex items-end">
                            <Button
                              variant="secondary"
                              type="button"
                              className="w-full justify-center"
                              onClick={() => setBevelPreview(group)}
                            >
                              <EyeIcon className="h-4 w-4 mr-2" />
                              View Bevel Design
                            </Button>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-500">
                              Backing Requirement <span className="text-error-500">*</span>
                            </label>
                            <div
                              className={clsx(
                                'mt-1 rounded-md border shadow-sm focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500',
                                getError(`pipe.${group.id}.backingRequirement`)
                                  ? 'border-error-500 focus-within:ring-error-500 focus-within:border-error-500'
                                  : 'border-gray-300'
                              )}
                            >
                              <InlineSelect
                                value={group.backingRequirement}
                                placeholder="Select"
                                fullWidth
                                buttonClassName="bg-white text-gray-700 rounded-md"
                                options={backingRequirementOptions.map((label) => ({
                                  value: label,
                                  label
                                }))}
                                onChange={(nextValue) =>
                                  updatePipeGroup(group.id, 'backingRequirement', nextValue)
                                }
                              />
                            </div>
                            {getError(`pipe.${group.id}.backingRequirement`) && (
                              <p className="mt-1 text-xs text-error-600">Required</p>
                            )}
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-500">
                              Backing Material <span className="text-error-500">*</span>
                            </label>
                            <Combobox
                              value={group.backingMaterial}
                              options={backingMaterialOptions}
                              placeholder="Ceramic / Steel ring"
                              onChange={(nextValue) =>
                                updatePipeGroup(group.id, 'backingMaterial', nextValue)
                              }
                              onCreateOption={async (value) => {
                                await addLabel('backing_material', value)
                              }}
                              className="mt-1"
                              inputClassName={clsx(
                                getError(`pipe.${group.id}.backingMaterial`) &&
                                  'border-error-500 focus:border-error-500 focus:ring-error-500'
                              )}
                            />
                            {getError(`pipe.${group.id}.backingMaterial`) && (
                              <p className="mt-1 text-xs text-error-600">Required</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {sectionState.materials && (
                      <div className="rounded-lg border border-gray-200 bg-white p-4">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-gray-900">Material specifications</p>
                          <Button variant="secondary" type="button" onClick={() => addMaterial(group.id)}>
                            <PlusCircleIcon className="h-4 w-4 mr-2" />
                            Add Material
                          </Button>
                        </div>

                        <div className="mt-3 space-y-3">
                          {group.materials.map((material) => (
                            <div
                              key={material.id}
                              className="grid gap-3 rounded-lg border border-gray-200 p-3 md:grid-cols-2"
                            >
                              <div>
                                <label className="text-xs font-medium text-gray-500">
                                  Material Spec <span className="text-error-500">*</span>
                                </label>
                                <Combobox
                                  value={material.materialSpec}
                                  options={materialSpecOptions}
                                  placeholder="Select or type"
                                  onChange={(nextValue) =>
                                    updateMaterial(group.id, material.id, 'materialSpec', nextValue)
                                  }
                                  onCreateOption={async (value) => {
                                    await addLabel('base_material_spec', value)
                                  }}
                                  className="mt-1"
                                  inputClassName={clsx(
                                    getError(`pipe.${group.id}.material.${material.id}.materialSpec`) &&
                                      'border-error-500 focus:border-error-500 focus:ring-error-500'
                                  )}
                                />
                                {getError(`pipe.${group.id}.material.${material.id}.materialSpec`) && (
                                  <p className="mt-1 text-xs text-error-600">Required</p>
                                )}
                              </div>
                              <div>
                                <label className="text-xs font-medium text-gray-500">
                                  Type / Grade <span className="text-error-500">*</span>
                                </label>
                                <Combobox
                                  value={material.typeGrade}
                                  options={gradeOptions}
                                  placeholder="Select or type"
                                  onChange={(nextValue) =>
                                    updateMaterial(group.id, material.id, 'typeGrade', nextValue)
                                  }
                                  onCreateOption={async (value) => {
                                    await addLabel('base_type_grade', value)
                                  }}
                                  className="mt-1"
                                  inputClassName={clsx(
                                    getError(`pipe.${group.id}.material.${material.id}.typeGrade`) &&
                                      'border-error-500 focus:border-error-500 focus:ring-error-500'
                                  )}
                                />
                                {getError(`pipe.${group.id}.material.${material.id}.typeGrade`) && (
                                  <p className="mt-1 text-xs text-error-600">Required</p>
                                )}
                              </div>
                              <div>
                                <label className="text-xs font-medium text-gray-500">
                                  Manufacture <span className="text-error-500">*</span>
                                </label>
                                <input
                                  className={clsx(
                                    'input-primary mt-1',
                                    getError(
                                      `pipe.${group.id}.material.${material.id}.manufacture`
                                    ) &&
                                      'border-error-500 focus:border-error-500 focus:ring-error-500'
                                  )}
                                  placeholder="Seamless"
                                  value={material.manufacture}
                                  onChange={(event) =>
                                    updateMaterial(
                                      group.id,
                                      material.id,
                                      'manufacture',
                                      event.target.value
                                    )
                                  }
                                />
                                {getError(
                                  `pipe.${group.id}.material.${material.id}.manufacture`
                                ) && <p className="mt-1 text-xs text-error-600">Required</p>}
                              </div>
                              <div>
                                <label className="text-xs font-medium text-gray-500">
                                  Source <span className="text-error-500">*</span>
                                </label>
                                <Combobox
                                  value={material.source}
                                  options={materialSourceOptions}
                                  placeholder="Mill A"
                                  onChange={(nextValue) =>
                                    updateMaterial(group.id, material.id, 'source', nextValue)
                                  }
                                  onCreateOption={async (value) => {
                                    await addLabel('base_source_mill', value)
                                  }}
                                  className="mt-1"
                                  inputClassName={clsx(
                                    getError(`pipe.${group.id}.material.${material.id}.source`) &&
                                      'border-error-500 focus:border-error-500 focus:ring-error-500'
                                  )}
                                />
                                {getError(`pipe.${group.id}.material.${material.id}.source`) && (
                                  <p className="mt-1 text-xs text-error-600">Required</p>
                                )}
                              </div>
                              <div className="md:col-span-2 flex justify-end">
                                <Button
                                  variant="ghost"
                                  className="text-error-600 hover:bg-error-50"
                                  type="button"
                                  onClick={() => removeMaterial(group.id, material.id)}
                                >
                                  <TrashIcon className="h-4 w-4 mr-2" />
                                  Remove Material
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}

          </div>
        </Card>
      )}
      </div>
      {bevelPreview && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-gray-900/50 p-4">
          <div className="w-full max-w-lg rounded-lg bg-white shadow-large">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div>
                <p className="text-sm text-gray-500">Bevel Design</p>
                <p className="text-lg font-semibold text-gray-900">
                  {bevelPreview.bevelType || 'Bevel type not selected'}
                </p>
              </div>
              <button
                type="button"
                className="rounded-full p-2 text-gray-400 hover:bg-gray-100"
                onClick={() => setBevelPreview(null)}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <svg viewBox="0 0 320 140" className="w-full h-28">
                  <rect x="20" y="30" width="120" height="80" fill="#ffffff" stroke="#94a3b8" strokeWidth="2" />
                  <rect x="180" y="30" width="120" height="80" fill="#ffffff" stroke="#94a3b8" strokeWidth="2" />
                  <line x1="140" y1="30" x2="180" y2="110" stroke="#1d4ed8" strokeWidth="2" />
                  <line x1="140" y1="110" x2="180" y2="30" stroke="#1d4ed8" strokeWidth="2" />
                  <text x="160" y="20" textAnchor="middle" fontSize="12" fill="#334155">Bevel</text>
                </svg>
              </div>
              <div className="grid gap-3 text-xs text-gray-500 sm:grid-cols-2">
                <div>
                  <p className="font-semibold text-gray-700">Nominal OD</p>
                  <p>{bevelPreview.nominalOd || 'TBD'} {bevelPreview.nominalOdUnit}</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-700">Nominal WT</p>
                  <p>{bevelPreview.nominalThickness || 'TBD'} {bevelPreview.nominalThicknessUnit}</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-700">Backing</p>
                  <p>{bevelPreview.backingRequirement || 'Not set'}</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-700">Backing Material</p>
                  <p>{bevelPreview.backingMaterial || 'Not set'}</p>
                </div>
              </div>
              <p className="text-xs text-gray-400">
                Diagram is a placeholder. Actual bevel geometry will be configurable in the next step.
              </p>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete project?"
        description={
          projectToEdit
            ? `Delete "${projectToEdit.name}" permanently. This action cannot be undone.`
            : 'Delete this project permanently. This action cannot be undone.'
        }
        confirmLabel="Delete"
        tone="danger"
        confirmBusy={deleteBusy}
        onCancel={() => {
          if (!deleteBusy) setDeleteDialogOpen(false)
        }}
        onConfirm={handleDeleteProject}
      />
    </div>
  )
}
